import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fmtAUD } from "@/lib/api";
import { FadeUp, RevealLine } from "@/components/Reveal";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, X } from "lucide-react";

const TIER_LABELS = {
  all: "All Devices",
  degoogled: "Tier 01 — Degoogled",
  secure: "Tier 02 — Secure",
  phantom: "Tier 03 — Phantom",
};

const SORTS = {
  tier: "Curated · by tier",
  price_asc: "Price · low → high",
  price_desc: "Price · high → low",
  name: "Name · A → Z",
};

function Row({ p, i }) {
  const fromPrice = p.base_price_aud || p.price_aud;
  return (
    <FadeUp delay={i * 0.05}>
      <Link
        to={`/product/${p.slug}`}
        data-testid={`shop-row-${p.slug}`}
        className="grid grid-cols-12 gap-4 md:gap-6 items-center py-8 border-b border-[#1A1A1A] group hover:bg-[#080808] px-4 md:px-6 transition-colors"
      >
        <div className="col-span-3 md:col-span-2 relative aspect-square overflow-hidden bg-[#080808]">
          <motion.img
            src={p.image_url}
            alt={p.name}
            className="absolute inset-0 w-full h-full object-cover img-clip"
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <div className="col-span-9 md:col-span-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="tier-dot degoogled" />
            <span className="tier-dot secure" />
            <span className="tier-dot phantom" />
            <span className="overline">3 tiers available</span>
          </div>
          <div className="font-display text-2xl md:text-3xl tracking-tight leading-tight">{p.name}</div>
          <div className="text-[#888] text-xs md:text-sm mt-2 max-w-md">{p.tagline}</div>
        </div>
        <div className="col-span-6 md:col-span-2 md:text-right">
          <div className="overline text-[#444]">From storage</div>
          <div className="text-[#EAEAEA] text-sm mt-1 font-mono">256GB · 512GB · 1TB</div>
        </div>
        <div className="col-span-6 md:col-span-2 md:text-right flex items-center md:justify-end gap-3">
          <div>
            <div className="overline text-[#444]">From</div>
            <div className="text-[#EAEAEA] text-lg md:text-xl">{fmtAUD(fromPrice)}</div>
          </div>
          <ArrowUpRight size={18} className="text-[#444] group-hover:text-[#EAEAEA] transition-colors" />
        </div>
      </Link>
    </FadeUp>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("tier");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const filtered = useMemo(() => {
    let out = [...products];
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((p) =>
        `${p.name} ${p.model} ${p.tagline} ${p.description}`.toLowerCase().includes(q)
      );
    }
    const pr = (p) => p.base_price_aud || p.price_aud || 0;
    if (sort === "price_asc") out.sort((a, b) => pr(a) - pr(b));
    else if (sort === "price_desc") out.sort((a, b) => pr(b) - pr(a));
    else if (sort === "name") out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [products, search, sort]);

  return (
    <div className="pt-32 pb-24">
      <div className="px-6 md:px-10">
        <div className="overline mb-6">Ξ 006 — Catalogue</div>
        <h1 className="font-display uppercase text-5xl md:text-8xl tracking-tight leading-[0.9]">
          <RevealLine i={0}>Every device.</RevealLine>
          <RevealLine i={1}>Every tier.</RevealLine>
        </h1>

        <div className="mt-12 border-t border-[#1A1A1A] pt-6">
          <div className="flex items-center gap-3 text-[#888] text-sm">
            <span className="tier-dot degoogled" /> Degoogled
            <span className="tier-dot secure ml-4" /> Secure
            <span className="tier-dot phantom ml-4" /> Phantom
            <span className="overline text-[#444] ml-4">— every device available in every tier</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center">
          <div className="md:col-span-7 relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by model, name, or feature…"
              className="field pl-10 pr-10"
              data-testid="shop-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#EAEAEA]" aria-label="clear" data-testid="shop-search-clear">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="md:col-span-4">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="field"
              data-testid="shop-sort"
            >
              {Object.entries(SORTS).map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 text-right overline text-[#888]" data-testid="shop-result-count">
            {filtered.length} / {products.length}
          </div>
        </div>

        <div className="mt-10">
          <div className="hidden md:grid grid-cols-12 gap-6 border-b border-[#1A1A1A] pb-4 px-6 overline">
            <div className="col-span-2">Device</div>
            <div className="col-span-6">Model</div>
            <div className="col-span-2 text-right">Storage</div>
            <div className="col-span-2 text-right">Price</div>
          </div>
          {products.length === 0 && <div className="text-[#888] py-12 text-center">Loading catalogue…</div>}
          {products.length > 0 && filtered.length === 0 && (
            <div className="text-[#666] py-16 text-center border border-[#1A1A1A]">
              No devices match. <button className="underline text-[#EAEAEA]" onClick={() => setSearch("")}>Reset search</button>
            </div>
          )}
          {filtered.map((p, i) => (
            <Row key={p.id} p={p} i={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
